"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const router = useRouter();

  const prefix = language === 'es' ? '/es' : '';

  const navLinks = [
    { label: t('nav_services'), href: `${prefix}/services`, sectionId: 'services' },
    { label: t('nav_ai_agents'), href: `${prefix}/ai-agents`, sectionId: 'ai-agents' },
    { label: t('nav_use_cases'), href: `${prefix}/use-cases`, sectionId: 'use-cases' },
    { label: t('nav_process'), href: `${prefix}/process`, sectionId: 'process' },
    { label: t('nav_about'), href: `${prefix}/about`, sectionId: 'why-devros' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, sectionId: string) => {
    e.preventDefault();
    setMenuOpen(false);
    // Check if we're already on a page that has the section rendered
    const el = document.getElementById(sectionId);
    if (el) {
      // Section exists on current page — just scroll
      el.scrollIntoView({ behavior: 'smooth' });
      // Still push the URL for GA tracking
      router.push(href);
    } else {
      // Navigate to the route (which will scroll on mount)
      router.push(href);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'py-3 bg-[rgba(6,13,26,0.92)] backdrop-blur-xl border-b border-[rgba(139,158,192,0.1)]'
          : 'py-5 bg-transparent'
      }`}
    >
      <div className="max-w-container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href={language === 'es' ? '/es' : '/home-page'} className="flex items-center group">
          <Image
            src="/assets/images/positivo---color-1773188764124.png"
            alt="Devros AI Logo"
            width={280}
            height={96}
            className="object-contain h-12 sm:h-16 lg:h-20 w-auto header-logo"
            priority
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href, link.sectionId)}
              className="text-sm font-medium text-muted hover:text-white transition-colors relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        {/* Right side: Language Toggle + CTA */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Language Toggle */}
          <div className="flex items-center rounded-xl border border-[rgba(139,158,192,0.2)] overflow-hidden">
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 text-xs font-bold transition-all duration-200 ${
                language === 'en' ?'bg-primary text-white' :'text-muted hover:text-white'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('es')}
              className={`px-3 py-1.5 text-xs font-bold transition-all duration-200 ${
                language === 'es' ?'bg-primary text-white' :'text-muted hover:text-white'
              }`}
            >
              ES
            </button>
          </div>

          <a
            href="https://calendly.com/devros-free-consultation?utm_source=website"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-6 py-2.5 rounded-xl text-sm"
          >
            <span>{t('nav_cta')}</span>
          </a>
        </div>

        {/* Mobile: Language Toggle + Hamburger */}
        <div className="lg:hidden flex items-center gap-3">
          <div className="flex items-center rounded-lg border border-[rgba(139,158,192,0.2)] overflow-hidden">
            <button
              onClick={() => setLanguage('en')}
              className={`px-2.5 py-1 text-[11px] font-bold transition-all duration-200 ${
                language === 'en' ? 'bg-primary text-white' : 'text-muted'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('es')}
              className={`px-2.5 py-1 text-[11px] font-bold transition-all duration-200 ${
                language === 'es' ? 'bg-primary text-white' : 'text-muted'
              }`}
            >
              ES
            </button>
          </div>
          <button
            className="flex flex-col gap-1.5 p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-400 overflow-hidden ${
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[rgba(6,13,26,0.97)] backdrop-blur-xl border-t border-[rgba(139,158,192,0.1)] px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href, link.sectionId)}
              className="text-base font-medium text-muted hover:text-white transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://calendly.com/devros-free-consultation?utm_source=website"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-6 py-3 rounded-xl text-sm text-center mt-2"
          >
            <span>{t('nav_cta')}</span>
          </a>
        </div>
      </div>
    </header>
  );
}